import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Alert from '../../../../components/Alert/Alert';
import Loading from '../../../../components/Loading/Loading';
import ConfirmationModal from '../../../../components/common/ConfirmationModal';
import { useAuth } from '../../../../contexts/AuthProvider';
import api from '../../../../api'; // pastikan path ini sesuai
import { ArrowLeft } from 'lucide-react';
// Import currency utilities
import { getCurrencySymbol, getCurrencyPrefix } from '../../../../utils/currencyUtils';
import useModalEffect from '../../../../hooks/useModalEffect';

const STATUS_REJECTED = 4; // Ganti dengan ID status "Rejected" di database Anda

const ProposalPreview = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { hasRole, hasPermission, can } = useAuth();
    const [alert, setAlert] = useState(null);
    const [loading, setLoading] = useState(true);
    const [proposalData, setProposalData] = useState(null);

    // Add showAlert helper function to match ProposalStage pattern
    const showAlert = (type, message) => {
        setAlert({ type, message });
        setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
    };

    // Helper function to format status descriptions to proper case
    const formatStatusDesc = (status) => {
        if (!status) return status;

        // Convert to string, replace underscores with spaces, convert to lowercase
        return status
            .toString()
            .replace(/_/g, ' ')
            .toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Badge color helpers (matching other pages)
    const getStatusBadge = (status) => {
        const colors = {
            'Rejected': 'danger',
            'Draft': 'secondary',
            'Need Approval': 'primary',
            'Revision': 'warning',
            'Approved': 'success',
        };
        return `badge badge-outline text-${colors[status] || 'secondary'}`;
    };

    const getProposalStatusBadge = (status) => {
        const colors = {
            'Draft': 'secondary',
            'Send To Client': 'primary',
            'Send to Client': 'primary',
            'Feedback': 'warning',
            'Accepted': 'success',
            'Rejected': 'danger',
        };
        return `badge badge-outline text-${colors[status] || 'secondary'}`;
    };

    // Tambahkan state berikut:
    const [loadingDocument, setLoadingDocument] = useState(false);
    const [documentError, setDocumentError] = useState(null);
    const [filledDocumentUrl, setFilledDocumentUrl] = useState(null);
    const [showDocument, setShowDocument] = useState(false);
    
    // ✅ INVESTMENT BREAKDOWN: Add state for investment breakdown and currency
    const [investmentBreakdown, setInvestmentBreakdown] = useState(null);
    const [showBreakdown, setShowBreakdown] = useState(false);
    const [proposalCurrencyCode, setProposalCurrencyCode] = useState('IDR');

    // State for revision modal
    const [showRevisionModal, setShowRevisionModal] = useState(false);
    useModalEffect(showRevisionModal);
    const [revisionComment, setRevisionComment] = useState('');
    const [revisionModalType, setRevisionModalType] = useState('revision'); // 'revision' for Manager/Director, 'feedback' for Sales

    // State for confirmation modals
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showAcceptModal, setShowAcceptModal] = useState(false);
    const [showDeclineModal, setShowDeclineModal] = useState(false);
    const [showDeclineProposalModal, setShowDeclineProposalModal] = useState(false);

    // ✅ INVESTMENT BREAKDOWN: Fetch investment breakdown from API
    const fetchInvestmentBreakdown = async () => {
        try {
            const response = await api.get(`/crm/proposals/${id}/breakdown`);
            if (response.data && response.data.years && response.data.breakdown) {
                setInvestmentBreakdown(response.data);
                setShowBreakdown(true);
            } else {
                setShowBreakdown(false);
                setInvestmentBreakdown(null);
            }
        } catch (error) {
            console.error('Error fetching investment breakdown:', error);
            setShowBreakdown(false);
            setInvestmentBreakdown(null);
            throw error; // Re-throw to allow fallback to local calculation
        }
    };

    // ✅ INVESTMENT BREAKDOWN: Calculate investment breakdown dynamically (fallback)
    const calculateInvestmentBreakdown = (proposalSections) => {
        let allYears = new Set();
        let breakdown = [];
        let totalsByYear = {};
        
        // Helper function to get years based on selected year options from table
        const getYearsForSelectedOption = (yearType, selectedYear, dynamicYearRange = null) => {
            if (yearType === 0) {
                // Every year - create a continuous range from min to max year based on Select Year items
                let years = new Set();
                if (dynamicYearRange && dynamicYearRange.length > 0) {
                    // For Every Year items, only use the years where Select Year items exist
                    dynamicYearRange.forEach(year => {
                        years.add(year);
                    });
                } else {
                    // Fallback to 5 years from current year if no Select Year items available
                    const currentYear = new Date().getFullYear();
                    for (let i = 0; i <= 4; i++) {
                        years.add(currentYear + i);
                    }
                }
                // Convert back to sorted array
                return Array.from(years).sort((a, b) => a - b);
            } else if (yearType === 1) {
                // Select Year - use the selectedYear from the table
                return [selectedYear || new Date().getFullYear()];
            } else {
                // Default fallback - calculate for 4 years from current year
                const currentYear = new Date().getFullYear();
                return [currentYear, currentYear + 1, currentYear + 2, currentYear + 3];
            }
        };
        
        // Convert proposal_sections to proposalServices format
        const proposalServices = proposalSections.map(section => ({
            service: section.type || 'Service',
            standards: (section.services || []).map(service => ({
                standard: service.standard || 'Standard',
                version: service.version || '', // ✅ TAMBAHAN: Include version from service data
                scope: service.scope || '', // Per-standard scope
                serviceItem: service.serviceItemId,
                yearType: service.yearType || 0,
                selectedYear: service.selectedYear || new Date().getFullYear(),
                qty: service.qty || 1,
                unit: service.unit || 'Unit',
                unitPrice: service.unitPrice || 0,
                productCategories: service.sub_services ? service.sub_services.map(child => ({
                    serviceItem: child.serviceItemId,
                    yearType: child.yearType || 0,
                    selectedYear: child.selectedYear || new Date().getFullYear(),
                    qty: child.qty || 1,
                    unit: child.unit || 'Unit',
                    unitPrice: child.unitPrice || 0
                })) : []
            }))
        }));
        
        // First pass: collect all years from non-every-year service items to determine dynamic range
        let dynamicYearRange = new Set();
        let hasSelectYearItems = false;

        proposalServices.forEach((section) => {
            (section.standards || []).forEach((standard) => {
                // Main row: service item (if present)
                if (standard.serviceItem) {
                    const yearType = standard.yearType || 0;
                    const selectedYear = standard.selectedYear || new Date().getFullYear();

                    if (yearType !== 0) { // Collect years from non-every-year items
                        hasSelectYearItems = true;
                        const itemYears = getYearsForSelectedOption(yearType, selectedYear);
                        itemYears.forEach(y => dynamicYearRange.add(y));
                    }
                }

                // Children rows: service items in productCategories
                if (standard.productCategories && Array.isArray(standard.productCategories)) {
                    standard.productCategories.forEach((pc) => {
                        if (pc.serviceItem) {
                            const yearType = pc.yearType || 0;
                            const selectedYear = pc.selectedYear || new Date().getFullYear();

                            if (yearType !== 0) { // Collect years from non-every-year items
                                hasSelectYearItems = true;
                                const itemYears = getYearsForSelectedOption(yearType, selectedYear);
                                itemYears.forEach(y => dynamicYearRange.add(y));
                            }
                        }
                    });
                }
            });
        });

        // If no Select Year items found, use current year as fallback
        if (!hasSelectYearItems) {
            dynamicYearRange.add(new Date().getFullYear());
        }
        
        // Convert dynamic year range to sorted array
        const dynamicYearArray = Array.from(dynamicYearRange).sort((a, b) => a - b);

        // If no dynamic year range found, create a default one
        if (dynamicYearArray.length === 0) {
            const currentYear = new Date().getFullYear();
            for (let i = 0; i <= 1; i++) {
                dynamicYearArray.push(currentYear + i);
            }
        }
        
        // Second pass: process all service items with the dynamic range
        proposalServices.forEach((section) => {
            const serviceLabel = section.service || `Service ${section.service}`;

            // Group service items by standard name to avoid duplicate standard headers
            const standardsMap = new Map();

            (section.standards || []).forEach((standard) => {
                const standardLabel = standard.standard || 'Standard';
                const version = standard.version || ''; // ✅ TAMBAHAN: Get version from standard data
                const scope = standard.scope || ''; // Per-standard scope

                // ✅ FIX: Use standard:version as key to group by standard+version combination
                const standardKey = standardLabel + (version && version.trim() !== '' ? ':' + version : '');

                // Get or create the standard entry
                if (!standardsMap.has(standardKey)) {
                    standardsMap.set(standardKey, {
                        standard: standardLabel,
                        version: version, // ✅ TAMBAHAN: Store version in the entry
                        scope: scope, // Per-standard scope
                        serviceItems: []
                    });
                }
                const standardEntry = standardsMap.get(standardKey);

                // Main row: service item (if present)
                if (standard.serviceItem) {
                    const itemLabel = `Service Item ${standard.serviceItem}`;
                    const price = (standard.unitPrice && standard.qty) ? parseFloat(standard.unitPrice || 0) * parseFloat(standard.qty || 1) : 0;
                    const yearType = standard.yearType || 0;
                    const selectedYear = standard.selectedYear || new Date().getFullYear();
                    const itemYears = getYearsForSelectedOption(yearType, selectedYear, dynamicYearArray);

                    // Add years to allYears set
                    itemYears.forEach(y => allYears.add(y));
                    // Map price to the correct years
                    itemYears.forEach(year => { totalsByYear[year] = (totalsByYear[year] || 0) + price; });
                    standardEntry.serviceItems.push({
                        serviceItem: itemLabel,
                        qty: standard.qty || 1,
                        unit: standard.unit || 'Unit',
                        prices: [price],
                        years: itemYears,
                        yearType: yearType,
                        selectedYear: selectedYear
                    });
                }
                
                // Children rows: service items in productCategories
                if (standard.productCategories && Array.isArray(standard.productCategories)) {
                    standard.productCategories.forEach((pc) => {
                        if (pc.serviceItem) {
                            const itemLabel = `Service Item ${pc.serviceItem}`;
                            const price = (pc.unitPrice && pc.qty) ? parseFloat(pc.unitPrice || 0) * parseFloat(pc.qty || 1) : 0;
                            const yearType = pc.yearType || 0;
                            const selectedYear = pc.selectedYear || new Date().getFullYear();
                            const itemYears = getYearsForSelectedOption(yearType, selectedYear, dynamicYearArray);

                            // Add years to allYears set
                            itemYears.forEach(y => allYears.add(y));
                            // Map price to the correct years
                            itemYears.forEach(year => { totalsByYear[year] = (totalsByYear[year] || 0) + price; });
                            standardEntry.serviceItems.push({
                                serviceItem: itemLabel,
                                qty: pc.qty || 1,
                                unit: pc.unit || 'Unit',
                                prices: [price],
                                years: itemYears,
                                yearType: yearType,
                                selectedYear: selectedYear
                            });
                        }
                    });
                }
            });

            // Convert map to array for the breakdown
            const standardsArr = Array.from(standardsMap.values());
            breakdown.push({ service: serviceLabel, standards: standardsArr });
        });
        
        const sortedYears = Array.from(allYears).sort((a, b) => a - b);
        if (sortedYears.length > 0) {
            setInvestmentBreakdown({
                years: sortedYears,
                breakdown,
                totals: sortedYears.map(y => totalsByYear[y] || 0)
            });
            setShowBreakdown(true);
        } else {
            // If no years found, hide the breakdown
            setShowBreakdown(false);
            setInvestmentBreakdown(null);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Simulate API call
                // await new Promise(resolve => setTimeout(resolve, 1000));
                // Replace with actual API call
                const res = await api.get(`/crm/proposals/detail/lead/${id}`);

                // Format status descriptions to proper case
                const fixedData = {
                    ...res.data,
                    statusDesc: formatStatusDesc(res.data.statusDesc),
                    statusApproval: formatStatusDesc(res.data.statusApproval || res.data.statusDesc),
                    statusProposalDesc: formatStatusDesc(res.data.statusProposalDesc)
                };
                setProposalData(fixedData);

                // ✅ CURRENCY: Handle currency like DetailProposal
                if (res.data) {
                    if (res.data.displayCurrencyId) {
                        try {
                            const currencyRes = await api.get(`/crm/currencies/${res.data.displayCurrencyId}`);
                            setProposalCurrencyCode(currencyRes.data.currencyCode || 'IDR');
                        } catch (error) {
                            console.warn('Could not fetch currency for displayCurrencyId:', res.data.displayCurrencyId, error);
                            setProposalCurrencyCode('IDR');
                        }
                    } else {
                        setProposalCurrencyCode('IDR');
                    }
                }

                // ✅ INVESTMENT BREAKDOWN: Try to fetch investment breakdown from API first
                try {
                    await fetchInvestmentBreakdown();
                } catch (error) {
                    // Fallback to local calculation if API fails
                    if (res.data && res.data.proposal_sections) {
                        calculateInvestmentBreakdown(res.data.proposal_sections);
                    }
                }

                // Otomatis load PDF jika ada
                if (res.data.proposalPdfFillPdf) {
                    await downloadAndDisplayFilledDocument(res.data);
                }
            } catch (error) {
                console.error('Error fetching proposal:', error);
                showAlert('danger', 'Failed to load proposal data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    // ✅ INVESTMENT BREAKDOWN: Recalculate when proposalData changes
    useEffect(() => {
        if (proposalData && proposalData.proposal_sections) {
            // Add a small delay to ensure all data is loaded
            const timer = setTimeout(() => {
                // Try API first, then fallback to local calculation
                fetchInvestmentBreakdown().catch(() => {
                    calculateInvestmentBreakdown(proposalData.proposal_sections);
                });
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [proposalData]);

    // Fungsi untuk download dan display filled document (mirip DetailProposal)
    const downloadAndDisplayFilledDocument = async (data = proposalData) => {
        setLoadingDocument(true);
        setDocumentError(null);

        try {
            let endpoint, contentType;
            if (data.proposalPdfFillPdf) {
                endpoint = `/crm/proposals/${data.id}/download-filled-document-pdf`;
                contentType = 'application/pdf';
            } else if (data.proposalPdfFill) {
                endpoint = `/crm/proposals/${data.id}/download-filled-document`;
                contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            } else {
                setDocumentError('No filled document available.');
                setLoadingDocument(false);
                return;
            }

            const response = await api.get(endpoint, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
            setFilledDocumentUrl(url);
            setShowDocument(true);
        } catch (error) {
            setDocumentError(`Failed to load document: ${error.message}`);
        } finally {
            setLoadingDocument(false);
        }
    };

    // Fungsi download
    const handleDownload = async (e) => {
        e.preventDefault();
        setLoadingDocument(true);
        setDocumentError(null);

        try {
            let endpoint, fileName, contentType;
            if (proposalData.proposalPdfFillPdf) {
                endpoint = `/crm/proposals/${proposalData.id}/download-filled-document-pdf`;
                fileName = `Proposal_${proposalData.proposalCode || proposalData.id}.pdf`;
                contentType = 'application/pdf';
            } else if (proposalData.proposalPdfFill) {
                endpoint = `/crm/proposals/${proposalData.id}/download-filled-document`;
                fileName = `Proposal_${proposalData.proposalCode || proposalData.id}.docx`;
                contentType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            } else {
                setDocumentError('No filled document available.');
                setLoadingDocument(false);
                return;
            }

            const response = await api.get(endpoint, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));

            // Buat elemen <a> untuk download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();

            // Optional: tampilkan preview juga
            setFilledDocumentUrl(url);
            setShowDocument(true);
        } catch (error) {
            setDocumentError(`Failed to download document: ${error.message}`);
        } finally {
            setLoadingDocument(false);
        }
    };

    // Fungsi print
    const handlePrint = () => {
        window.print();
    };

    const handleBack = () => {
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate('/proposals');
        }
    };

    const handleApprove = () => {
        console.log('🔍 Debug - handleApprove called, setting showApproveModal to true');
        setShowApproveModal(true);
    };

    const handleAccept = () => {
        console.log('🔍 Debug - handleAccept called, setting showAcceptModal to true');
        setShowAcceptModal(true);
    };

    const handleDecline = () => {
        console.log('🔍 Debug - handleDecline called, setting showDeclineModal to true');
        setShowDeclineModal(true);
    };

    const handleDeclineProposal = () => {
        console.log('🔍 Debug - handleDeclineProposal called, setting showDeclineProposalModal to true');
        setShowDeclineProposalModal(true);
    };

    // Approve (for Managers/Directors)
    const handleConfirmApprove = async () => {
        // Validate status approval is "Need Approval"
        const isNeedApproval = proposalData?.status === 3 ||
            proposalData?.statusDesc === 'Need Approval' ||
            proposalData?.statusApproval === 'Need Approval';

        if (!isNeedApproval) {
            showAlert('warning', `Only proposals with "Need Approval" status can be approved (current status: ${proposalData?.statusApproval || proposalData?.statusDesc || 'Draft'})`);
            return;
        }

        setLoading(true);
        try {
            if (!proposalData?.id) throw new Error('Proposal ID not found');

            // For role non-SALES (MANAGER/DIRECTOR), gunakan flow approval biasa
            await api.post(`/crm/proposals/${proposalData.id}/approve`, null, {
                params: { comments: 'Approved via preview' }
            });
            showAlert('success', 'Proposal approved successfully');
            setShowApproveModal(false);
            cleanupModalBackdrop();
            setProposalData(prev => prev ? {
                ...prev,
                statusDesc: 'Approved',
                status: 2, // Approved (sesuai pola: 2 = "Approved")
                statusApproval: 'Approved'
            } : prev);
        } catch (error) {
            showAlert('danger', 'Failed to approve proposal: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Accept (for Sales)
    const handleConfirmAccept = async () => {
        // Validate status proposal is "Send to Client" or "Feedback"
        const isSendToClient = proposalData?.statusProposal === 2 ||
            proposalData?.statusProposalDesc === 'Send To Client' ||
            proposalData?.statusProposalDesc === 'Send to Client';
        const isFeedback = proposalData?.statusProposal === 5 ||
            proposalData?.statusProposalDesc === 'Feedback';
        const canAccept = isSendToClient || isFeedback;

        if (!canAccept) {
            showAlert('warning', `Only proposals with "Send to Client" or "Feedback" status can be accepted (current status: ${proposalData?.statusProposalDesc || 'Draft'})`);
            return;
        }

        setLoading(true);
        try {
            if (!proposalData?.id) throw new Error('Proposal ID not found');

            // Debug: Log the data being sent
            console.log('Debug - id from useParams:', id);
            console.log('Debug - proposalData:', proposalData);
            console.log('Debug - proposalData.leadId:', proposalData?.leadId);
            console.log('Debug - proposalData.id:', proposalData?.id);
            console.log('Debug - proposalData.lead_id:', proposalData?.lead_id);
            console.log('Debug - proposalData.lead:', proposalData?.lead);

            // Try to get the leadId from various possible sources
            // Prioritize URL parameter (id) since it's always the leadId
            let endpointId = id || proposalData.leadId || proposalData.lead_id || proposalData.lead?.id;

            if (!endpointId) {
                throw new Error('No valid ID found for the proposal. Cannot proceed with status update.');
            }

            // Ensure endpointId is a string or number
            endpointId = String(endpointId);

            console.log('Debug - endpointId:', endpointId);
            console.log('Debug - full endpoint:', `/crm/proposals/${endpointId}/update-status-no-workflow`);

            await api.put(`/crm/proposals/${endpointId}/update-status-no-workflow`, {
                status: 2, // Tetap Approved
                statusDesc: "Approved",
                statusProposal: 3, // Accepted (sesuai pola: 3 = "Accepted")
                statusProposalDesc: "Accepted"
            });

            showAlert('success', 'Proposal accepted successfully by Sales');
            setShowAcceptModal(false);
            cleanupModalBackdrop();

            // ✅ TAMBAHAN: Refresh proposal data untuk mendapatkan status terbaru
            try {
                const refreshedRes = await api.get(`/crm/proposals/detail/lead/${endpointId}`);
                if (refreshedRes.data) {
                    // Format status descriptions to proper case
                    const fixedData = {
                        ...refreshedRes.data,
                        statusDesc: formatStatusDesc(refreshedRes.data.statusDesc),
                        statusApproval: formatStatusDesc(refreshedRes.data.statusApproval || refreshedRes.data.statusDesc),
                        statusProposalDesc: formatStatusDesc(refreshedRes.data.statusProposalDesc)
                    };
                    setProposalData(fixedData);
                    console.log('✓ Proposal data refreshed with Accepted status');
                }
            } catch (refreshError) {
                console.warn('⚠️ Failed to refresh proposal data:', refreshError);
                // Fallback: Update local state
                setProposalData(prev => prev ? {
                    ...prev,
                    statusDesc: 'Approved',
                    statusApproval: 'Approved',
                    status: 2,
                    statusProposalDesc: 'Accepted',
                    statusProposal: 3
                } : prev);
            }
        } catch (error) {
            showAlert('danger', 'Failed to accept proposal: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to clean up modal backdrop
    const cleanupModalBackdrop = () => {
        // Remove any stuck modal backdrops

        // Remove modal-open class from body
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
    };

    const handleRevisionCancel = () => {
        setShowRevisionModal(false);
        setRevisionComment('');
        setRevisionModalType('revision'); // Reset to default
        cleanupModalBackdrop();
    };

    // Handle revision modal show/hide with Bootstrap
    ;


    // Decline (for Managers/Directors)
    const handleConfirmDecline = async () => {
        // Validate status approval is "Need Approval"
        const isNeedApproval = proposalData?.status === 3 ||
            proposalData?.statusDesc === 'Need Approval' ||
            proposalData?.statusApproval === 'Need Approval';

        if (!isNeedApproval) {
            showAlert('warning', `Only proposals with "Need Approval" status can be declined (current status: ${proposalData?.statusApproval || proposalData?.statusDesc || 'Draft'})`);
            return;
        }

        setLoading(true);
        try {
            if (!proposalData?.id) throw new Error('Proposal ID not found');

            // For role non-SALES (MANAGER/DIRECTOR), gunakan flow rejection biasa
            await api.post(`/crm/proposals/${proposalData.id}/reject`, null, {
                params: { comments: 'Declined via preview' }
            });
            showAlert('warning', 'Proposal has been declined');
            setShowDeclineModal(false);
            cleanupModalBackdrop();

            // ✅ TAMBAHAN: Refresh proposal data untuk mendapatkan status terbaru
            try {
                const refreshedRes = await api.get(`/crm/proposals/detail/lead/${proposalData.id}`);
                if (refreshedRes.data) {
                    // Format status descriptions to proper case
                    const fixedData = {
                        ...refreshedRes.data,
                        statusDesc: formatStatusDesc(refreshedRes.data.statusDesc),
                        statusApproval: formatStatusDesc(refreshedRes.data.statusApproval || refreshedRes.data.statusDesc),
                        statusProposalDesc: formatStatusDesc(refreshedRes.data.statusProposalDesc)
                    };
                    setProposalData(fixedData);
                    console.log('✓ Proposal data refreshed with Rejected status');
                }
            } catch (refreshError) {
                console.error('Failed to refresh proposal data:', refreshError);
                // Fallback to local state update
                setProposalData(prev => prev ? {
                    ...prev,
                    statusDesc: 'Rejected',
                    status: 4, // Rejected (sesuai pola: 4 = "Rejected")
                    statusApproval: 'Rejected'
                } : prev);
            }
        } catch (error) {
            showAlert('danger', 'Failed to decline proposal: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Decline Proposal (for Sales)
    const handleConfirmDeclineProposal = async () => {
        // Validate status proposal is "Send to Client"
        const isSendToClient = proposalData?.statusProposal === 2 ||
            proposalData?.statusProposalDesc === 'Send To Client' ||
            proposalData?.statusProposalDesc === 'Send to Client';

        if (!isSendToClient) {
            showAlert('warning', `Only proposals with "Send to Client" status can be declined (current status: ${proposalData?.statusProposalDesc || 'Draft'})`);
            return;
        }

        setLoading(true);
        try {
            if (!proposalData?.id) throw new Error('Proposal ID not found');

            // Debug: Log the data being sent
            console.log('Debug - Declining proposal for Sales role');
            console.log('Debug - id from useParams:', id);
            console.log('Debug - proposalData:', proposalData);

            // Try to get the leadId from various possible sources
            // Prioritize URL parameter (id) since it's always the leadId
            let endpointId = id || proposalData.leadId || proposalData.lead_id || proposalData.lead?.id;

            if (!endpointId) {
                throw new Error('No valid ID found for the proposal. Cannot proceed with status update.');
            }

            // Ensure endpointId is a string or number
            endpointId = String(endpointId);

            console.log('Debug - endpointId:', endpointId);
            console.log('Debug - full endpoint:', `/crm/proposals/${endpointId}/update-status-no-workflow`);

            await api.put(`/crm/proposals/${endpointId}/update-status-no-workflow`, {
                status: 2, // Tetap Approved
                statusDesc: "Approved",
                statusProposal: 4, // Rejected (sesuai pola: 4 = "Rejected")
                statusProposalDesc: "Rejected"
            });

            showAlert('warning', 'Proposal has been declined by Sales');
            setShowDeclineProposalModal(false);
            cleanupModalBackdrop();

            // ✅ TAMBAHAN: Refresh proposal data untuk mendapatkan status terbaru
            try {
                const refreshedRes = await api.get(`/crm/proposals/detail/lead/${endpointId}`);
                if (refreshedRes.data) {
                    // Format status descriptions to proper case
                    const fixedData = {
                        ...refreshedRes.data,
                        statusDesc: formatStatusDesc(refreshedRes.data.statusDesc),
                        statusApproval: formatStatusDesc(refreshedRes.data.statusApproval || refreshedRes.data.statusDesc),
                        statusProposalDesc: formatStatusDesc(refreshedRes.data.statusProposalDesc)
                    };
                    setProposalData(fixedData);
                    console.log('✓ Proposal data refreshed with Rejected status');
                }
            } catch (refreshError) {
                console.warn('⚠️ Failed to refresh proposal data:', refreshError);
                // Fallback: Update local state
                setProposalData(prev => prev ? {
                    ...prev,
                    statusDesc: 'Approved',
                    statusApproval: 'Approved',
                    status: 2,
                    statusProposalDesc: 'Rejected',
                    statusProposal: 4
                } : prev);
            }
        } catch (error) {
            showAlert('danger', 'Failed to decline proposal: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Request Revision (oleh approver: MANAGER/DIRECTOR)
    const handleRevisionSubmit = async () => {
        if (!revisionComment.trim()) {
            showAlert('danger', 'Please enter a revision comment');
            return;
        }

        // ✅ TAMBAHAN: Cek apakah modal type adalah 'feedback' (Sales) atau 'revision' (Manager/Director)
        if (revisionModalType === 'feedback') {
            // Validate status proposal is "Send to Client" for Sales
            const isSendToClient = proposalData?.statusProposal === 2 ||
                proposalData?.statusProposalDesc === 'Send To Client' ||
                proposalData?.statusProposalDesc === 'Send to Client';

            if (!isSendToClient) {
                showAlert('warning', `Only proposals with "Send to Client" status can receive feedback (current status: ${proposalData?.statusProposalDesc || 'Draft'})`);
                return;
            }
        }

        setLoading(true);
        try {
            if (!proposalData?.id) throw new Error('Proposal ID not found');

            // ✅ TAMBAHAN: Cek apakah modal type adalah 'feedback' (Sales) atau 'revision' (Manager/Director)
            if (revisionModalType === 'feedback') {
                // ✅ TAMBAHAN: Update status proposal menjadi 'Feedback' untuk ROLE_SALES

                // Debug: Log the data being sent
                console.log('Debug - Providing feedback for Sales role');
                console.log('Debug - id from useParams:', id);
                console.log('Debug - proposalData:', proposalData);
                console.log('Debug - revisionComment:', revisionComment);

                // Try to get the leadId from various possible sources
                // Prioritize URL parameter (id) since it's always the leadId
                let endpointId = id || proposalData.leadId || proposalData.lead_id || proposalData.lead?.id;

                if (!endpointId) {
                    throw new Error('No valid ID found for the proposal. Cannot proceed with status update.');
                }

                // Ensure endpointId is a string or number
                endpointId = String(endpointId);

                console.log('Debug - endpointId:', endpointId);
                console.log('Debug - full endpoint:', `/crm/proposals/${endpointId}/update-status-no-workflow`);

                await api.put(`/crm/proposals/${endpointId}/update-status-no-workflow`, {
                    status: 2, // Tetap Approved
                    statusDesc: "Approved",
                    statusProposal: 5, // Feedback (sesuai pola: 5 = "Feedback")
                    statusProposalDesc: "Feedback"
                });

                showAlert('warning', 'Feedback has been submitted successfully');
                setShowRevisionModal(false);
                setRevisionComment('');
                cleanupModalBackdrop();

                // ✅ TAMBAHAN: Refresh proposal data untuk mendapatkan status terbaru
                try {
                    const refreshedRes = await api.get(`/crm/proposals/detail/lead/${endpointId}`);
                    if (refreshedRes.data) {
                        // Format status descriptions to proper case
                        const fixedData = {
                            ...refreshedRes.data,
                            statusDesc: formatStatusDesc(refreshedRes.data.statusDesc),
                            statusApproval: formatStatusDesc(refreshedRes.data.statusApproval || refreshedRes.data.statusDesc),
                            statusProposalDesc: formatStatusDesc(refreshedRes.data.statusProposalDesc)
                        };
                        setProposalData(fixedData);
                        console.log('✓ Proposal data refreshed with Feedback status');
                    }
                } catch (refreshError) {
                    console.warn('⚠️ Failed to refresh proposal data:', refreshError);
                    // Fallback: Update local state
                    setProposalData(prev => prev ? {
                        ...prev,
                        statusDesc: 'Approved',
                        statusApproval: 'Approved',
                        status: 2,
                        statusProposalDesc: 'Feedback',
                        statusProposal: 5
                    } : prev);
                }
            } else {
                // ✅ TAMBAHAN: Untuk role non-SALES (MANAGER/DIRECTOR), gunakan flow revision biasa
                // Validate status approval is "Need Approval" for Manager/Director
                const isNeedApproval = proposalData?.status === 3 ||
                    proposalData?.statusDesc === 'Need Approval' ||
                    proposalData?.statusApproval === 'Need Approval';

                if (!isNeedApproval) {
                    showAlert('warning', `Only proposals with "Need Approval" status can request revision (current status: ${proposalData?.statusApproval || proposalData?.statusDesc || 'Draft'})`);
                    setShowRevisionModal(false);
                    setRevisionComment('');
                    cleanupModalBackdrop();
                    setLoading(false);
                    return;
                }

                // Use the working update-status-no-workflow endpoint instead of the failing /revision/request
                await api.put(`/crm/proposals/${proposalData.leadId || id}/update-status-no-workflow`, {
                    status: 5, // Revision status
                    statusDesc: "Revision",
                    statusProposal: proposalData?.statusProposal || 1, // Keep existing proposal status
                    statusProposalDesc: proposalData?.statusProposalDesc || "Draft"
                });

                showAlert('warning', 'Revision requested');
                setShowRevisionModal(false);
                setRevisionComment('');
                cleanupModalBackdrop();

                // ✅ TAMBAHAN: Refresh proposal data untuk mendapatkan status terbaru
                try {
                    const refreshedRes = await api.get(`/crm/proposals/detail/lead/${proposalData.id}`);
                    if (refreshedRes.data) {
                        // Format status descriptions to proper case
                        const fixedData = {
                            ...refreshedRes.data,
                            statusDesc: formatStatusDesc(refreshedRes.data.statusDesc),
                            statusApproval: formatStatusDesc(refreshedRes.data.statusApproval || refreshedRes.data.statusDesc),
                            statusProposalDesc: formatStatusDesc(refreshedRes.data.statusProposalDesc)
                        };
                        setProposalData(fixedData);
                        console.log('✓ Proposal data refreshed with Revision status');
                    }
                } catch (refreshError) {
                    console.warn('⚠️ Failed to refresh proposal data:', refreshError);
                    // Fallback: Update local state
                    setProposalData(prev => prev ? {
                        ...prev,
                        statusDesc: 'Revision',
                        status: 5, // Revision (sesuai pola: 5 = "Revision")
                        statusApproval: 'Revision'
                    } : prev);
                }
            }
        } catch (error) {
            showAlert('danger', 'Failed to request revision: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRevision = (type = 'revision') => {
        setRevisionModalType(type); // 'revision' for Manager/Director, 'feedback' for Sales
        setShowRevisionModal(true);
    };

    // Helper untuk status dan permissions
    const isFinal = proposalData?.statusDesc === 'Approved' || proposalData?.statusDesc === 'Rejected' || proposalData?.statusDesc === 'Revision';
    const can_approve = can('can_approve', 'proposal'); // Permission for Manager/Director approval actions
    const can_accept = can('can_accept', 'proposal'); // Permission for Sales acceptance actions
    const hasDocument = proposalData?.proposalPdfFillPdf || proposalData?.proposalPdfFill;

    // ✅ Check if status proposal is "Send to Client"
    const isSendToClient = proposalData?.statusProposal === 2 ||
        proposalData?.statusProposalDesc === 'Send To Client' ||
        proposalData?.statusProposalDesc === 'Send to Client';

    // ✅ Check if status proposal is "Feedback"
    const isFeedback = proposalData?.statusProposal === 5 ||
        proposalData?.statusProposalDesc === 'Feedback';

    // ✅ Check if proposal can be accepted (Send to Client or Feedback)
    const canAcceptProposal = isSendToClient || isFeedback;

    // ✅ Check if status approval is "Need Approval"
    const isNeedApproval = proposalData?.status === 3 ||
        proposalData?.statusDesc === 'Need Approval' ||
        proposalData?.statusApproval === 'Need Approval';

    // Sales buttons only enabled when user has "can accept" permission AND status proposal is "Send to Client" or "Feedback"
    const canSalesAccept = can_accept && canAcceptProposal;

    // Manager/Director buttons only enabled when user has "can approve" permission AND status approval is "Need Approval"
    const canManagerApprove = can_approve && isNeedApproval;

    // ✅ TAMBAHAN: Debug logging untuk membantu troubleshooting
    console.log('🔍 Debug Permission and Status Validation:', {
        can_accept,
        can_approve,
        isSendToClient,
        isFeedback,
        canAcceptProposal,
        isNeedApproval,
        canSalesAccept,
        canManagerApprove,
        statusProposal: proposalData?.statusProposal,
        statusProposalDesc: proposalData?.statusProposalDesc,
        statusApproval: proposalData?.statusApproval,
        statusDesc: proposalData?.statusDesc,
        status: proposalData?.status
    });

    // ✅ TAMBAHAN: Debug modal state
    console.log('🔍 Debug Modal State:', {
        showApproveModal,
        showAcceptModal,
        showDeclineModal,
        showDeclineProposalModal,
        showRevisionModal
    });

    if (loading) {
        return <Loading />;
    }

    if (!proposalData) {
        return (
            <div className="container-xl">
                <div className="text-center p-5">
                    <h3>Proposal not found</h3>
                </div>
            </div>
        );
    }

    const getQuotedInvestmentTotalForYear = (year) => (investmentBreakdown?.breakdown || []).reduce((serviceTotal, serviceRow) => (
        serviceTotal + (serviceRow.standards || []).reduce((standardTotal, stdRow) => (
            standardTotal + (stdRow.serviceItems || []).reduce((itemTotal, item) => {
                if (!Array.isArray(item.years) || !item.years.includes(year)) return itemTotal;
                return itemTotal + (Number(item.unitPrice || 0) * Number(item.qty ?? 1));
            }, 0)
        ), 0)
    ), 0);
    const showConversionColumns = String(proposalCurrencyCode || 'IDR').toUpperCase() !== 'IDR';
    const breakdownBaseColumnCount = showConversionColumns ? 4 : 3;

    return (
        <>
            {alert && (
                <Alert
                    type={alert.type}
                    message={alert.message}
                    onClose={() => setAlert(null)}
                />
            )}

            {/* Approve Confirmation Modal (for Managers/Directors) */}
            <ConfirmationModal
                isOpen={showApproveModal}
                onClose={() => {
                    setShowApproveModal(false);
                    cleanupModalBackdrop();
                }}
                onConfirm={handleConfirmApprove}
                title="Approve Proposal"
                message="Are you sure you want to approve this proposal? This will change the proposal status to 'Approved' and allow it to proceed to the next stage."
                confirmText="Approve"
                confirmVariant="success"
                modalId="modal-approve-confirmation"
            />

            {/* Accept Confirmation Modal (for Sales) */}
            <ConfirmationModal
                isOpen={showAcceptModal}
                onClose={() => {
                    setShowAcceptModal(false);
                    cleanupModalBackdrop();
                }}
                onConfirm={handleConfirmAccept}
                title="Accept Proposal"
                message="Are you sure you want to accept this proposal? This will mark the proposal as 'Accepted' and indicate client approval."
                confirmText="Accept"
                confirmVariant="primary"
                modalId="modal-accept-confirmation"
            />

            {/* Decline Confirmation Modal (for Managers/Directors) */}
            <ConfirmationModal
                isOpen={showDeclineModal}
                onClose={() => {
                    setShowDeclineModal(false);
                    cleanupModalBackdrop();
                }}
                onConfirm={handleConfirmDecline}
                title="Decline Proposal"
                message="Are you sure you want to decline this proposal? This will mark the proposal as 'Rejected' in the approval system and this action cannot be undone."
                confirmText="Decline"
                confirmVariant="danger"
                modalId="modal-decline-confirmation"
            />

            {/* Decline Proposal Confirmation Modal (for Sales) */}
            <ConfirmationModal
                isOpen={showDeclineProposalModal}
                onClose={() => {
                    setShowDeclineProposalModal(false);
                    cleanupModalBackdrop();
                }}
                onConfirm={handleConfirmDeclineProposal}
                title="Decline Proposal"
                message="Are you sure you want to decline this proposal? This will mark the proposal as 'Rejected' and indicate client rejection."
                confirmText="Decline"
                confirmVariant="danger"
                modalId="modal-decline-proposal-confirmation"
            />

            {/* Revision Modal */}
            <div
                className={`modal modal-blur fade${showRevisionModal ? ' show' : ''}`} style={{ display: showRevisionModal ? 'block' : 'none' }}
                id="modal-revision"
                tabIndex="-1"
                role="dialog"
            >
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">
                                {revisionModalType === 'feedback' ? 'Provide Feedback' : 'Request Revision'}
                            </h5>
                            <button type="button" className="btn-close" onClick={() => setShowRevisionModal(false)} aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="mb-3">
                                <label htmlFor="revisionComment" className="form-label">
                                    {revisionModalType === 'feedback' ? 'Feedback Comment' : 'Revision Comment'}
                                </label>
                                <textarea
                                    id="revisionComment"
                                    className="form-control"
                                    rows="4"
                                    placeholder={revisionModalType === 'feedback' ?
                                        "Please provide your feedback about this proposal..." :
                                        "Please provide details about the required revision..."
                                    }
                                    value={revisionComment}
                                    onChange={(e) => setRevisionComment(e.target.value)}
                                ></textarea>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowRevisionModal(false)}
                                onClick={handleRevisionCancel}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-warning"
                                onClick={handleRevisionSubmit}
                                disabled={loading}
                            >
                                {loading ? 'Submitting...' : (revisionModalType === 'feedback' ? 'Submit Feedback' : 'Submit Revision')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="container-xl">
                {/* Header with actions */}
                <div className="page-header d-print-none">
                    <div className="container-xl">
                        <div className="row g-2 align-items-center">
                            <div className="col">
                                <div className="d-flex align-items-center">
                                    <a className="btn btn-outline-white d-none d-sm-inline-block me-3" onClick={handleBack}>
                                        <ArrowLeft className="icon icon-tabler icon-tabler-arrow-left" />
                                        Back
                                    </a>
                                </div>
                                <div className="mt-2">
                                    <h4 className="page-title mb-1">Proposal Preview</h4>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="text-muted">Status Approval:</span>
                                        <span className={getStatusBadge(proposalData?.statusApproval || proposalData?.statusDesc || 'Draft')}>
                                            {proposalData?.statusApproval || proposalData?.statusDesc || 'Draft'}
                                        </span>
                                        <span className="text-muted ms-3">Status Proposal:</span>
                                        <span className={getProposalStatusBadge(proposalData?.statusProposalDesc || 'Draft')}>
                                            {proposalData?.statusProposalDesc || 'Draft'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="col-auto ms-auto d-print-none">
                                {/* Manager/Director Buttons - Show only for users with "can approve" permission */}
                                {can_approve && (
                                    <div className="btn-list">
                                        <button
                                            className="btn btn-success d-none d-sm-inline-block"
                                            onClick={handleApprove}
                                            disabled={!canManagerApprove}
                                            title={!isNeedApproval ? `Only "Need Approval" status can be approved (current: ${proposalData?.statusApproval || proposalData?.statusDesc || 'Draft'})` : 'Approve this proposal'}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-check" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                <path d="M5 12l5 5l10 -10"></path>
                                            </svg>
                                            Approve
                                        </button>
                                        <button
                                            className="btn btn-danger d-none d-sm-inline-block"
                                            onClick={handleDecline}
                                            disabled={!canManagerApprove}
                                            title={!isNeedApproval ? `Only "Need Approval" status can be declined (current: ${proposalData?.statusApproval || proposalData?.statusDesc || 'Draft'})` : 'Decline this proposal'}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-x" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                <path d="M18 6l-12 12"></path>
                                                <path d="M6 6l12 12"></path>
                                            </svg>
                                            Decline
                                        </button>
                                        <button
                                            className="btn d-none d-sm-inline-block"
                                            onClick={() => handleRevision('revision')}
                                            style={{ backgroundColor: '#ffa726', borderColor: '#ffa726', color: 'white' }}
                                            disabled={!canManagerApprove}
                                            title={!isNeedApproval ? `Only "Need Approval" status can request revision (current: ${proposalData?.statusApproval || proposalData?.statusDesc || 'Draft'})` : 'Request revision for this proposal'}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-edit" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 0 2 -2v-1"></path>
                                                <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"></path>
                                                <path d="M16 5l3 3"></path>
                                            </svg>
                                            Revision
                                        </button>
                                    </div>
                                )}

                                {/* Sales View Buttons - Show only for users with "can accept" permission */}
                                <div className="btn-list mt-2">
                                    {can_accept && (
                                        <>
                                            <button
                                                className="btn btn-success d-none d-sm-inline-block"
                                                onClick={handleAccept}
                                                disabled={!canSalesAccept}
                                                title={!can_accept ? "You don't have permission to accept proposals" :
                                                    !canAcceptProposal ? `Only "Send to Client" or "Feedback" status can be accepted (current: ${proposalData?.statusProposalDesc || 'Draft'})` :
                                                        "Accept this proposal"}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-check" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                    <path d="M5 12l5 5l10 -10"></path>
                                                </svg>
                                                Accept Proposal
                                            </button>
                                            <button
                                                className="btn btn-danger d-none d-sm-inline-block"
                                                onClick={handleDeclineProposal}
                                                disabled={!canSalesAccept}
                                                title={!can_accept ? "You don't have permission to decline proposals" :
                                                    !isSendToClient ? `Only "Send to Client" status can be declined (current: ${proposalData?.statusProposalDesc || 'Draft'})` :
                                                        "Decline this proposal"}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-x" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                    <path d="M18 6l-12 12"></path>
                                                    <path d="M6 6l12 12"></path>
                                                </svg>
                                                Decline Proposal
                                            </button>
                                            <button
                                                className="btn d-none d-sm-inline-block"
                                                onClick={() => handleRevision('feedback')}
                                                style={{ backgroundColor: '#ffa726', borderColor: '#ffa726', color: 'white' }}
                                                disabled={!canSalesAccept}
                                                title={!can_accept ? "You don't have permission to provide feedback" :
                                                    !isSendToClient ? `Only "Send to Client" status can receive feedback (current: ${proposalData?.statusProposalDesc || 'Draft'})` :
                                                        "Provide feedback for this proposal"}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-edit" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                                    <path d="M7 7h-1a2 2 0 0 0 -2 2v9a2 2 0 0 0 2 2h9a2 2 0 0 1 -2 -2v-1"></path>
                                                    <path d="M20.385 6.585a2.1 2.1 0 0 0 -2.97 -2.97l-8.415 8.385v3h3l8.385 -8.415z"></path>
                                                    <path d="M16 5l3 3"></path>
                                                </svg>
                                                Feedback
                                            </button>
                                        </>
                                    )}
                                </div>

                                {/* Common Buttons - Always visible for all roles */}
                                <div className="btn-list">
                                    <a className="btn btn-primary d-none d-sm-inline-block" href="#" onClick={handleDownload}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-file-download me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                            <path d="M14 3v4a1 1 0 0 0 1 1h4"></path>
                                            <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z"></path>
                                            <path d="M12 17v-6"></path>
                                            <path d="M9.5 14.5l2.5 2.5l2.5 -2.5"></path>
                                        </svg>
                                        Download PDF
                                    </a>
                                    <a className="btn btn-secondary d-none d-sm-inline-block" href="#" onClick={handlePrint}>
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-printer me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                            <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2"></path>
                                            <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4"></path>
                                            <path d="M7 13m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v4a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z"></path>
                                        </svg>
                                        Print
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* PDF Preview */}
                <div className="page-body">
                    <div className="container-fluid">
                        {loadingDocument ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2">Loading document...</p>
                            </div>
                        ) : documentError ? (
                            <div className="alert alert-danger">{documentError}</div>
                        ) : (filledDocumentUrl && showDocument) ? (
                            <iframe
                                src={filledDocumentUrl}
                                title="Filled Proposal Document"
                                width="100%"
                                height="100vh"
                                style={{ border: 'none', minHeight: '800px' }}
                            />
                        ) : (
                            <div className="text-center p-5 text-muted">
                                {proposalData.proposalPdfFillPdf
                                    ? 'Click "Download PDF" to view the filled proposal document.'
                                    : proposalData.proposalPdfFill
                                        ? 'Only DOCX available. Download to view.'
                                        : 'No filled document available.'}
                            </div>
                        )}
                        
                        {/* ✅ INVESTMENT BREAKDOWN: Investment Breakdown Table - Always visible below PDF */}
                        <div className="card mt-4 w-100">
                            <div className="card-header">
                                <h5 className="card-title mb-0">
                                    Investment Breakdown
                                    {investmentBreakdown && ` (${investmentBreakdown.years.length} Years)`}
                                </h5>
                            </div>
                            <div className="card-body">
                                {investmentBreakdown ? (
                                    <div className="table-responsive">
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Details</th>
                                                    <th style={{ width: '80px', minWidth: '80px' }}>Unit</th>
                                                    <th style={{ width: '60px', minWidth: '60px' }}>Qty</th>
                                                    {showConversionColumns && <th style={{ width: '110px', minWidth: '110px' }}>Exchange Rate</th>}
                                                    {investmentBreakdown.years.map(year => (
                                                        <th key={year}>
                                                            {year}
                                                            <div className="text-muted fw-normal small">Quoted / IDR</div>
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {investmentBreakdown.breakdown.map((serviceRow, sIdx) => (
                                                    <React.Fragment key={sIdx}>
                                                        <tr>
                                                            <td className="fw-bold" colSpan={breakdownBaseColumnCount + investmentBreakdown.years.length}>{serviceRow.service}</td>
                                                        </tr>
                                                        {serviceRow.standards.map((stdRow, stdIdx) => (
                                                            <React.Fragment key={stdIdx}>
                                                                <tr>
                                                                    <td style={{ paddingLeft: 24, fontWeight: 'bold' }} colSpan={breakdownBaseColumnCount + investmentBreakdown.years.length}>
                                                                        {stdRow.standard}
                                                                        {stdRow.version && stdRow.version.trim() !== '' && `:${stdRow.version}`}
                                                                    </td>
                                                                </tr>
                                                                {stdRow.scope && stdRow.scope.trim() !== '' && (
                                                                    <tr>
                                                                        <td style={{ paddingLeft: 24 }} colSpan={breakdownBaseColumnCount + investmentBreakdown.years.length}>
                                                                            <span className="text-muted small">Scope: {stdRow.scope}</span>
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                                {stdRow.serviceItems.map((item, cidx) => (
                                                                    <tr key={cidx}>
                                                                        <td style={{ paddingLeft: 48 }}>
                                                                            {item.serviceItem}
                                                                        </td>
                                                                        <td>{item.unit || '-'}</td>
                                                                        <td>{item.qty || '-'}</td>
                                                                        {showConversionColumns && <td>{Number(item.exchangeRate || 1).toLocaleString('id-ID')}</td>}
                                                                        {investmentBreakdown.years.map((year) => {
                                                                            const yearIdx = item.years.indexOf(year);
                                                                            const itemCurrency = String(item.currencyCode || proposalCurrencyCode || 'IDR').toUpperCase();
                                                                            const sourceAmount = Number(item.unitPrice || 0) * Number(item.qty ?? 1);
                                                                            const convertedAmount = Number(item.prices?.[yearIdx] || 0);
                                                                            return (
                                                                                <td key={year} className="align-middle">
                                                                                    {yearIdx !== -1 && item.prices && item.prices[yearIdx] !== undefined ? (
                                                                                        showConversionColumns ? <div className="rounded border bg-light px-2 py-1">
                                                                                            <div className="d-flex justify-content-between align-items-center gap-2">
                                                                                                <span className="text-muted small">{itemCurrency}</span>
                                                                                                <span className="fw-semibold">{getCurrencyPrefix(itemCurrency)}{sourceAmount.toLocaleString('id-ID')}</span>
                                                                                            </div>
                                                                                            <div className="border-top mt-1 pt-1 d-flex justify-content-between align-items-center gap-2">
                                                                                                <span className="text-muted small">IDR</span>
                                                                                                <span className="text-success fw-semibold">Rp {convertedAmount.toLocaleString('id-ID')}</span>
                                                                                            </div>
                                                                                        </div> : <span className="fw-semibold">Rp {convertedAmount.toLocaleString('id-ID')}</span>
                                                                                    ) : '-'}
                                                                                </td>
                                                                            );
                                                                        })}
                                                                    </tr>
                                                                ))}
                                                            </React.Fragment>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr>
                                                    <th colSpan={showConversionColumns ? 4 : 3}>Total</th>
                                                    {investmentBreakdown.totals.map((total, i) => (
                                                        <th key={i} className="text-end">
                                                            {showConversionColumns ? <div className="rounded border bg-light px-2 py-1">
                                                                <div><span className="text-muted small">{proposalCurrencyCode}</span> {getCurrencyPrefix(proposalCurrencyCode)}{getQuotedInvestmentTotalForYear(investmentBreakdown.years[i]).toLocaleString('id-ID')}</div>
                                                                <div className="border-top mt-1 pt-1 text-success"><span className="text-muted small">IDR</span> Rp {total.toLocaleString('id-ID')}</div>
                                                            </div> : <span className="fw-semibold">Rp {total.toLocaleString('id-ID')}</span>}
                                                        </th>
                                                    ))}
                                                </tr>
                                                <tr>
                                                    <th colSpan={showConversionColumns ? 4 : 3}>Grand Total</th>
                                                    <th colSpan={investmentBreakdown.totals.length} className="text-end">
                                                        {showConversionColumns ? <div className="rounded border bg-light px-2 py-1 d-inline-block">
                                                            <div><span className="text-muted small">{proposalCurrencyCode}</span> {getCurrencyPrefix(proposalCurrencyCode)}{investmentBreakdown.years.reduce((sum, year) => sum + getQuotedInvestmentTotalForYear(year), 0).toLocaleString('id-ID')}</div>
                                                            <div className="border-top mt-1 pt-1 text-success"><span className="text-muted small">IDR</span> Rp {investmentBreakdown.totals.reduce((sum, t) => sum + t, 0).toLocaleString('id-ID')}</div>
                                                        </div> : <span className="fw-semibold">Rp {investmentBreakdown.totals.reduce((sum, t) => sum + t, 0).toLocaleString('id-ID')}</span>}
                                                    </th>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-4">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calculator mb-3" width="48" height="48" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                            <path d="M4 19m0 1a1 1 0 0 1 1 -1h14a1 1 0 0 1 1 1v1a1 1 0 0 1 -1 1h-14a1 1 0 0 1 -1 -1z"></path>
                                            <path d="M7 7m0 1a1 1 0 0 1 1 -1h10a1 1 0 0 1 1 1v1a1 1 0 0 1 -1 1h-10a1 1 0 0 1 -1 -1z"></path>
                                            <path d="M7 15m0 1a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v1a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1z"></path>
                                            <path d="M15 7l4 0"></path>
                                            <path d="M15 11l4 0"></path>
                                            <path d="M7 11l3 0"></path>
                                            <path d="M7 15l4 0"></path>
                                        </svg>
                                        <h5 className="text-muted">Investment Breakdown</h5>
                                        <p className="text-muted mb-0">The investment breakdown will appear here once the proposal data is loaded and calculated.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProposalPreview;
